import { useCallback, useState } from "react";

export default function useModalController({
    create,      // (values) => Promise
    update,      // (id, values) => Promise
    remove,      // (id) => Promise
    mapEditValues = (row) => row ?? {}, // normalize row -> values
}) {
    const [modal, setModal] = useState({ open: false, mode: "create", entity: null, values: {} });
    const [busy, setBusy] = useState(false);

    const openCreate = useCallback(() =>
        setModal({ open: true, mode: "create", entity: null, values: {} }), []);

    const openEdit = useCallback((entity) =>
        setModal({ open: true, mode: "edit", entity, values: mapEditValues(entity) }), [mapEditValues]);

    const openDelete = useCallback((entity) =>
        setModal({ open: true, mode: "delete", entity, values: {} }), []);

    const setValue = useCallback((k, v) =>
        setModal(m => ({ ...m, values: { ...m.values, [k]: v } })), []);

    const close = useCallback(() =>
        setModal({ open: false, mode: "create", entity: null, values: {} }), []);

    const commit = useCallback(async () => {
        if (busy) return;
        setBusy(true);
        try {
            const { mode, entity, values } = modal;
            if (mode === "delete") await remove?.(entity.id);
            else if (mode === "edit") await update?.(entity.id, values);
            else await create?.(values);
            close();
        } finally { setBusy(false); }
    }, [busy, modal, create, update, remove, close]);

    return { modal, busy, actions: { openCreate, openEdit, openDelete, setValue, close, commit } };
}
